/**
 * MeasurementEquipmentForm Component
 * Form for creating/editing measurement equipment
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  MeasurementEquipmentFormData,
  MeasurementEquipmentFormProps,
  MeasurementEquipmentStatus,
} from '../../../types/measurementEquipment';
import { occupationalRiskService } from '../../../services/occupationalRiskService';
import { OccupationalRisk, OccupationalRiskStatus } from '../../../types/occupationalRisk';
import { useCompanyContext } from '../../../hooks/useCompanyContext';
import { CompanySelectionModal, CompanyInfoBox } from '../../../components/forms';
import { useCompanySelection } from '../../../hooks/useCompanySelection';

const RISK_TYPE_LABELS: Record<string, string> = {
  FISICO: 'Físicos',
  QUIMICO: 'Químicos',
  BIOLOGICO: 'Biológicos',
  ERGONOMICO: 'Ergonômicos',
  ACIDENTE: 'Acidentes',
  OUTRO: 'Outros',
};

export const MeasurementEquipmentForm: React.FC<MeasurementEquipmentFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = Boolean(initialData?.id);
  const [risks, setRisks] = useState<OccupationalRisk[]>([]);
  const [loadingDependencies, setLoadingDependencies] = useState(true);

  const { effectiveCompanyId } = useCompanyContext();

  const { modalOpen, selectedCompany, showInfoBox, openModal, handleSelect } =
    useCompanySelection({
      isEditMode: isEditing,
      currentCompanyId: initialData?.companyId?.toString(),
    });

  const today = new Date().toISOString().split('T')[0];
  const nextYear = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MeasurementEquipmentFormData>({
    defaultValues: {
      isGlobal: initialData?.isGlobal ?? false,
      companyId: initialData?.companyId,
      name: initialData?.name ?? '',
      serialNumber: initialData?.serialNumber ?? '',
      manufacturer: initialData?.manufacturer ?? '',
      model: initialData?.model ?? '',
      occupationalRiskIds: initialData?.occupationalRisks?.map(r => r.id) ?? [],
      eSocialCode: initialData?.eSocialCode ?? '',
      calibrationDate: initialData?.calibrationDate ?? today,
      validityDate: initialData?.validityDate ?? nextYear,
      methodology: initialData?.methodology ?? '',
      detectsHarmfulAgents: initialData?.detectsHarmfulAgents ?? true,
      status: initialData?.status ?? MeasurementEquipmentStatus.ACTIVE,
    },
  });

  const isGlobal = watch('isGlobal');

  // Load risks
  useEffect(() => {
    const loadDependencies = async () => {
      try {
        setLoadingDependencies(true);
        const risksResponse = await occupationalRiskService.getOccupationalRisks({
          limit: 1000,
          status: OccupationalRiskStatus.ACTIVE,
        });
        setRisks(risksResponse.risks ?? []);
      } catch (error) {
        console.error('Error loading dependencies:', error);
      } finally {
        setLoadingDependencies(false);
      }
    };
    loadDependencies();
  }, []);

  // Clear companyId when isGlobal is set to true
  useEffect(() => {
    if (isGlobal) {
      setValue('companyId', undefined);
    }
  }, [isGlobal, setValue]);

  // Sync companyId from selectedCompany (modal selection) or effectiveCompanyId (header)
  useEffect(() => {
    if (isGlobal) return;
    if (selectedCompany) {
      setValue('companyId', Number(selectedCompany.id));
    } else if (effectiveCompanyId) {
      setValue('companyId', effectiveCompanyId);
    }
  }, [isGlobal, selectedCompany, effectiveCompanyId, setValue]);

  const onFormSubmit = (data: MeasurementEquipmentFormData) => {
    onSubmit(data);
  };

  if (loadingDependencies) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {showInfoBox && selectedCompany && !isGlobal && (
        <CompanyInfoBox
          companyName={selectedCompany.name}
          onEdit={openModal}
        />
      )}
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={12}>
            <Card>
              <CardHeader title="Informações Básicas" />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Global Toggle */}
                  <Grid size={12}>
                    <Controller
                      name="isGlobal"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                              disabled={isEditing}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">
                                Equipamento Global
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Equipamentos globais são visíveis para todas as empresas
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />
                  </Grid>

                  {/* Name */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: 'Nome é obrigatório', minLength: { value: 2, message: 'Mínimo 2 caracteres' } }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nome do Equipamento *"
                          fullWidth
                          error={!!errors.name}
                          helperText={errors.name?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Serial Number */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="serialNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Número de Série"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Manufacturer */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="manufacturer"
                      control={control}
                      rules={{ required: 'Fabricante é obrigatório' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Fabricante *"
                          fullWidth
                          error={!!errors.manufacturer}
                          helperText={errors.manufacturer?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Model */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="model"
                      control={control}
                      rules={{ required: 'Modelo é obrigatório' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Modelo *"
                          fullWidth
                          error={!!errors.model}
                          helperText={errors.model?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* eSocial Code */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="eSocialCode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Código eSocial"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Calibration Information */}
          <Grid size={12}>
            <Card>
              <CardHeader title="Calibração" />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Calibration Date */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="calibrationDate"
                      control={control}
                      rules={{ required: 'Data de calibração é obrigatória' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="date"
                          label="Data de Calibração *"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.calibrationDate}
                          helperText={errors.calibrationDate?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Validity Date */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="validityDate"
                      control={control}
                      rules={{ required: 'Data de validade é obrigatória' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="date"
                          label="Data de Validade *"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.validityDate}
                          helperText={errors.validityDate?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Technical Specifications */}
          <Grid size={12}>
            <Card>
              <CardHeader title="Especificações Técnicas" />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Methodology */}
                  <Grid size={12}>
                    <Controller
                      name="methodology"
                      control={control}
                      rules={{ required: 'Metodologia é obrigatória' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Metodologia de Medição *"
                          fullWidth
                          multiline
                          rows={3}
                          error={!!errors.methodology}
                          helperText={errors.methodology?.message}
                          placeholder="Ex: Dosimetria conforme NHO-01 da FUNDACENTRO"
                        />
                      )}
                    />
                  </Grid>

                  {/* Occupational Risks (multiple) */}
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="occupationalRiskIds"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          options={[...risks].sort((a, b) =>
                            (RISK_TYPE_LABELS[a.riskType] ?? a.riskType).localeCompare(
                              RISK_TYPE_LABELS[b.riskType] ?? b.riskType
                            )
                          )}
                          groupBy={(option) => RISK_TYPE_LABELS[option.riskType] ?? option.riskType}
                          getOptionLabel={(option) => option.riskFactorName}
                          isOptionEqualToValue={(opt, val) => opt.id === val.id}
                          value={risks.filter(r => field.value.includes(r.id))}
                          onChange={(_, selected) => field.onChange(selected.map(r => r.id))}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Riscos Ocupacionais Associados"
                              placeholder={field.value.length === 0 ? 'Selecione um ou mais riscos' : ''}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={option.riskFactorName}
                                size="small"
                                {...getTagProps({ index })}
                                key={option.id}
                              />
                            ))
                          }
                        />
                      )}
                    />
                  </Grid>

                  {/* Detects Harmful Agents */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="detectsHarmfulAgents"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                            />
                          }
                          label="Detecta Agentes Nocivos"
                        />
                      )}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select {...field} label="Status">
                            <MenuItem value={MeasurementEquipmentStatus.ACTIVE}>Ativo</MenuItem>
                            <MenuItem value={MeasurementEquipmentStatus.INACTIVE}>Inativo</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                startIcon={<CancelIcon />}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <CompanySelectionModal open={modalOpen && !isGlobal} onSelect={handleSelect} />
    </>
  );
};

export default MeasurementEquipmentForm;
